/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import { RefObject, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { Field, Form, Formik, FormikProps } from 'formik'
import styled from 'styled-components'
import * as Yup from 'yup'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Form as NXUIForm } from '@nexigendigital/nxui'
import { useCancelServiceMutation, useCheckCancelableServiceQuery } from 'api/service'
import { RootState } from 'store/store'
import { RefundMethod } from 'models/enums'
import { CancelService } from 'models/service'
import { aud, titleCase } from 'helpers/utils'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    form: RefObject<FormikProps<CancelService>>,
    close: () => void
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function CancelForm({ form, close }: Props) {
    const location = useLocation()
    const id = parseInt(location.pathname?.split('/')[2], 10)
    const [cancelService, { isLoading, isSuccess, isError }] = useCancelServiceMutation()
    const { data } = useCheckCancelableServiceQuery(id)
    const { appSettings: { provider: { provider_name } } } = useSelector((state: RootState) => state.app)
    const reasons = [
        {
            label: 'No longer need this service',
            value: 'No longer need this service'
        },
        {
            label: `Transferring to another ${provider_name} service`,
            value: `Transferring to another ${provider_name} service`
        },
        {
            label: 'Unhappy with the performance of the hosting service',
            value: 'Unhappy with the performance of the hosting service'
        },
        {
            label: 'The quality of support provided was unsatisfactory',
            value: 'The quality of support provided was unsatisfactory'
        },
        {
            label: 'The price and inclusions are no longer competitive',
            value: 'The price and inclusions are no longer competitive'
        },
        {
            label: 'Other',
            value: 'Other'
        }
    ]
    const time = [
        {
            label: 'Cancel ASAP',
            value: 'immediate'
        },
        {
            label: 'End of billing cycle',
            value: 'end_of_billing_cycle'
        }
    ]

    /**********************************************************************************************************
     *   SIDE EFFECTS
     **********************************************************************************************************/
    useEffect(() => {
        if ((isSuccess || isError) && form.current) {
            form.current.setSubmitting(false)
            form.current.resetForm()
        }

        if (isSuccess) {
            close()
        }
    }, [form, isSuccess, isError])

    return (
        <Formik
            innerRef={form}
            initialValues={{
                reason: reasons[0].value,
                reason_comment: '',
                type: 'immediate',
                refund_method: RefundMethod.None,
                confirm_delete: false,
                confirm_backup: false,
                account_name: '',
                branch_number: '',
                account_number: ''
            }}
            validationSchema={
                Yup.object({
                    reason: Yup.string(),
                    reason_comment: Yup.string(),
                    type: Yup.string(),
                    refund_method: Yup.string(),
                    confirm_delete: Yup.bool(),
                    confirm_backup: Yup.bool(),
                    account_name: Yup.string()
                        .when('refund_method', {
                            is: 'manual',
                            then: Yup.string().required('Required')
                        }),
                    branch_number: Yup.number()
                        .when('refund_method', {
                            is: 'manual',
                            then: Yup.number()
                                .typeError('Invalid branch number')
                                .required('Required')
                                .min(100000, 'Invalid branch number')
                                .max(999999, 'Invalid branch number')
                        }),
                    account_number: Yup.number()
                        .when('refund_method', {
                            is: 'manual',
                            then: Yup.number()
                                .typeError('Invalid account number')
                                .required('Required')
                        })
                })
            }
            onSubmit={({ refund_method, account_name, branch_number, account_number, ...rest }) => {
                if (refund_method === RefundMethod.Manual) {
                    cancelService({ id, refund_method, account_name, branch_number, account_number, ...rest })
                } else {
                    cancelService({ id, refund_method, ...rest })
                }
            }}
        >
            {({ values }) => (
                <Form id={'cancelForm'}>
                    <Field
                        label={'Primary Reason for Cancelling Service'}
                        name={'reason'}
                        type={'select'}
                        disabled={isLoading}
                        list={reasons}
                        component={NXUIForm.SelectField}
                    />
                    <Field
                        label={'Additional Comments'}
                        name={'reason_comment'}
                        component={NXUIForm.TextAreaField}
                    />
                    <Field
                        label={'When would you like your service to be terminated?'}
                        name={'type'}
                        type={'select'}
                        disabled={isLoading}
                        list={time}
                        component={NXUIForm.SelectField}
                    />
                    {data?.refundable &&
                        <Field
                            label={'Refund Methods'}
                            name={'refund_method'}
                            type={'select'}
                            disabled={isLoading}
                            list={data.refund_methods.map((method: string) => {
                                return {
                                    label: titleCase(method.replace(/_/g, ' ')),
                                    value: method
                                }
                            })}
                            component={NXUIForm.SelectField}
                        />

                    }
                    {values.refund_method === RefundMethod.Manual &&
                        <>
                            <Field
                                label={'Account Name'}
                                name={'account_name'}
                                type={'text'}
                                disabled={isLoading}
                                component={NXUIForm.InputField}
                            />
                            <Field
                                label={'Branch Number'}
                                name={'branch_number'}
                                type={'text'}
                                disabled={isLoading}
                                component={NXUIForm.InputField}
                            />
                            <Field
                                label={'Account Number'}
                                name={'account_number'}
                                type={'text'}
                                disabled={isLoading}
                                component={NXUIForm.InputField}
                            />
                        </>
                    }
                    {(data?.refundable && data?.refund_amount) &&
                        <RefundAmount>
                            <Label>Refund Amount</Label>
                            <Value>{aud(data?.refund_amount)}</Value>
                        </RefundAmount>
                    }
                    <Field
                        label={'I confirm all data stored as part of this service, including backups, will be deleted.'}
                        name={'confirm_delete'}
                        type={'checkbox'}
                        component={NXUIForm.CheckboxField}
                    />
                    <Field
                        label={'I confirm any data I require is backed up externally.'}
                        name={'confirm_backup'}
                        type={'checkbox'}
                        component={NXUIForm.CheckboxField}
                    />
                </Form>
            )}
        </Formik>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const RefundAmount = styled.div`
    padding: 19px 1.5rem;
    margin-bottom: 15px;
    margin-left: -1.5rem;
    margin-right: -1.5rem;
    display: flex;
    justify-content: space-between;
    background-color: ${props => (props.theme['background--200'])};
    font-size: .875rem;
`

const Label = styled.div`
    color: ${props => props.theme['text--100']};
`

const Value = styled.div`
    color: ${props => props.theme['text--200']};
`
